import React, { useEffect, useState } from "react";
import { Spin, Button, Divider, Popconfirm } from "antd";
import { faTable } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
// REDUX
import {
  usePostFavoriteGridsMutation,
  usePutFavoriteGridsMutation,
  useDeleteFavoriteGridsMutation,
  useLazyGetFavoriteGridQuery,
} from "../../../redux/monitoring/monitoringAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// CONTEXT
import { useWizard } from "../WizardContext";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";
import { WizardStep } from "../utils/wizardInvalidation";
import { useWizardStepDraft } from "../utils/useWizardStepDraft";
// STORE
import { RootState } from "../../../app/store";
// WIZARD COMPONENTS
import WizardExportButton from "../components/WizardExportButton";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";

export default function StepMonitorGrid() {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, setState: setWizardState, markStepCompleted, goToStep } = useWizard();
  const access = useCurrentWizardStepAccess();

  const isEditMode = !!wizardState.monitorGridId;

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [gridName, setGridName] = useState("Default Grid");
  const [saving, setSaving] = useState(false);

  // ************************************************ */
  // DRAFT PERSISTENCE ******************************* */
  // Restores the in-progress grid name on page reload / browser-restart so
  // the operator doesn't have to retype it. Auto-save runs 750ms after
  // each keystroke and rides the FlatBuffer wizard-drafts transport (see
  // wizardDraftsAPI.ts). No JSON fallback path.
  //
  // Verification: gridName must be a non-empty string to bother persisting;
  // empty draft snapshots are skipped so a transient "Default Grid → ''
  // → typed-name" sequence cannot blow away a previously-good draft mid-
  // edit.
  useWizardStepDraft<{ gridName: string }>({
    orgId: wizardState.organizationId,
    step: WizardStep.MonitorGrid,
    scopeId: "monitor-grid",
    scopeType: "step",
    draft: { gridName },
    onRestore: (saved) => {
      if (typeof saved.gridName === "string" && saved.gridName.trim() !== "") {
        setGridName(saved.gridName);
      }
    },
    validate: (snapshot) => snapshot.gridName.trim() !== "",
  });

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostGrid,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostFavoriteGridsMutation();

  const [triggerPutGrid, { isError: isErrorPut, error: errorPut }] = usePutFavoriteGridsMutation();

  const [triggerGetGrid] = useLazyGetFavoriteGridQuery();
  const [triggerDeleteGrid, { isLoading: isLoadingDelete }] = useDeleteFavoriteGridsMutation();

  // ************************************************ */
  // FUNCTIONS ************************************* */
  const onChangeFormValue = (newValue: string, variableName: string) => {
    if (variableName === "gridName") setGridName(newValue);
  };

  const isFormValid = () => gridName.trim() !== "" && !!wizardState.organizationId;

  const handleSave = async () => {
    if (!isFormValid()) return;
    const token = await GETJwtToken();
    const body = {
      name: gridName,
      organization_id: wizardState.organizationId,
      grid: JSON.stringify([]),
      query: "",
    };
    triggerPostGrid({ body, token });
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !wizardState.monitorGridId) return;
    setSaving(true);
    const token = await GETJwtToken();
    try {
      await triggerPutGrid({
        id: wizardState.monitorGridId,
        body: {
          name: gridName,
          organization_id: wizardState.organizationId,
          grid: JSON.stringify([]),
          query: "",
        },
        token,
      }).unwrap();
      markStepCompleted(WizardStep.MonitorGrid);
    } catch {
      // Error shown by GlobalAlert2
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGrid = async () => {
    if (!wizardState.monitorGridId) return;
    const token = await GETJwtToken();
    try {
      await triggerDeleteGrid({
        id: wizardState.monitorGridId,
        token,
      }).unwrap();
      setWizardState((prev) => ({ ...prev, monitorGridId: null }));
      // Intentionally do NOT unmark this step on delete. MonitorGrid is
      // optional — the operator may delete a grid and choose to skip
      // the step entirely. Unmarking would block Next until they
      // either re-create a grid or click Skip, which contradicts the
      // "optional means always satisfied" model.
      setGridName("Default Grid");
    } catch {
      // Error shown by GlobalAlert2
    }
  };

  const handleSkip = () => {
    markStepCompleted(WizardStep.MonitorGrid);
    goToStep(WizardStep.Review);
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */
  // Auto-mark this step complete whenever the operator is on it.
  // The monitor grid is optional — the full grid editor lives on the
  // monitoring dashboard after setup, so most operators leave the
  // wizard's grid step alone entirely. Saving a real grid still goes
  // through handleSave / handleUpdate; this just unblocks Next when
  // the operator has nothing to configure here. Depending on
  // `currentStep` re-fires this if an upstream step unmarked us.
  useEffect(() => {
    if (wizardState.currentStep === WizardStep.MonitorGrid) {
      markStepCompleted(WizardStep.MonitorGrid);
    }
  }, [wizardState.currentStep, markStepCompleted]);

  useEffect(() => {
    if (isSuccessPost && dataPost) {
      const gridData = dataPost.data || dataPost;
      const gridId = gridData.id;
      if (!gridId) return;

      setWizardState((prev) => ({
        ...prev,
        monitorGridId: gridId,
      }));
      markStepCompleted(WizardStep.MonitorGrid);
    }
  }, [isSuccessPost, dataPost]);

  // Hydrate form fields when resuming a draft wizard with an existing monitorGridId
  useEffect(() => {
    if (!wizardState.monitorGridId) return;
    const fetchGrid = async () => {
      const token = await GETJwtToken();
      try {
        const result = await triggerGetGrid({
          idGrid: String(wizardState.monitorGridId),
          token,
        }).unwrap();
        if (result && result.data) {
          setGridName(result.data.name || "Default Grid");
          markStepCompleted(WizardStep.MonitorGrid);
        }
      } catch {
        // Grid fetch failed — leave default name
      }
    };
    fetchGrid();
  }, [wizardState.monitorGridId]);

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingPost || isLoadingDelete || saving}>
      <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
        {/* MONITOR GRID FORM */}
        <DrawerRowDivider
          icon={faTable}
          title={isEditMode ? "Edit Monitor Grid" : "Create Monitor Grid"}
        />
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-6 mt-3">
              <FormInputText
                inputValue={gridName}
                inputLabelName={t("general.name")}
                inputOnChange={onChangeFormValue}
                inputVariableName="gridName"
                isRequired
                disabledInput={false}
                formularyType={FormularyType.PAGE}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 mt-3">
              <span className="generalStyles__inputLabel" style={{ color: "#888" }}>
                The full grid layout editor is available in the monitoring dashboard after setup.
              </span>
            </div>
          </div>
        </div>

        {/* SAVE / DELETE / SKIP BUTTONS */}
        <Divider />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Backend stage 11 = MonitorGrid. Previously hardcoded as
                10 (pre-ChannelModifiers numbering), which downloaded the
                Actions scope. */}
            <WizardExportButton stage={11} orgId={wizardState.organizationId} />
            {wizardState.monitorGridId && (
              <WizardOpGate op="delete">
                <Popconfirm
                  title="Delete this grid?"
                  onConfirm={handleDeleteGrid}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger loading={isLoadingDelete} size="large">
                    Delete Grid
                  </Button>
                </Popconfirm>
              </WizardOpGate>
            )}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={handleSkip} size="large">
              Skip Grid
            </Button>
            <Button
              type="primary"
              onClick={isEditMode ? handleUpdate : handleSave}
              disabled={!isFormValid() || (isEditMode ? !access.update : !access.create)}
              loading={isLoadingPost || saving}
              size="large"
            >
              {isEditMode ? "Update Grid" : "Save Grid"}
            </Button>
          </div>
        </div>

        {/* ALERTS */}
        <GlobalAlert2
          isError={isErrorPost}
          isSuccess={isSuccessPost}
          requestType="POST"
          error={errorPost}
          name="Monitor Grid"
        />
        <GlobalAlert2
          isError={isErrorPut}
          isSuccess={false}
          requestType="PUT"
          error={errorPut}
          name="Monitor Grid"
        />
      </div>
    </Spin>
  );
}
